import { ItemCategory } from "../types";
import { isSellListingModelFieldEnabled } from "@/lib/listing/listingIdentityPolicy";

export type SizeInputType = "text" | "dimensions" | "select";

/** When set, the size text box accepts digits only (decimal allows one dot). */
export type SizeValueKind = "decimal" | "integer";
export type SizeUnit =
  | "inches"
  | "liters"
  | "KVA"
  | "kg"
  | "ft"
  | "HP"
  | "varies";

export interface SizeConfig {
  enabled: boolean;
  label: string;
  unit: SizeUnit;
  inputType: SizeInputType;
  /** Enforced numeric spec fields: restrict keystrokes to numbers. */
  valueKind?: SizeValueKind;
  options?: string[]; // For select type (fixed option lists)
  placeholder?: string;
}

export interface CategoryFieldConfig {
  brand: boolean;
  model: boolean;
  size: SizeConfig;
}

/**
 * Category-specific field configurations
 * Determines which fields to show based on category
 */
/**
 * Category field visibility for sell / preference surfaces.
 * Model is off across categories while sell identity policy defers model
 * collection (`listingIdentityPolicy.SELL_LISTING_COLLECTS_MODEL`).
 */
export const categoryFieldConfigs: Record<ItemCategory, CategoryFieldConfig> = {
  electronics: {
    brand: true,
    model: false,
    size: {
      enabled: true,
      label: "Size/Capacity",
      unit: "varies",
      inputType: "text",
      placeholder: "e.g., 55 inches, 200L, 5KVA",
    },
  },
  furniture: {
    brand: false,
    model: false,
    size: {
      enabled: true,
      label: "Dimensions",
      unit: "ft",
      inputType: "dimensions",
      placeholder: "e.g., 4ft x 6ft",
    },
  },
  kitchen: {
    brand: true,
    model: false,
    size: {
      enabled: true,
      label: "Capacity",
      unit: "liters",
      inputType: "text",
      placeholder: "e.g., 200L, 50L",
    },
  },
  bedding: {
    brand: false,
    model: false,
    size: {
      enabled: true,
      label: "Dimensions",
      unit: "ft",
      inputType: "dimensions",
      placeholder: "e.g., 4ft x 6ft or 4 x 6",
    },
  },
  fitness: {
    brand: true,
    model: false,
    size: {
      enabled: false,
      label: "Size",
      unit: "varies",
      inputType: "text",
    },
  },
  fashion: {
    brand: true,
    model: false,
    size: {
      enabled: false,
      label: "Size",
      unit: "varies",
      inputType: "text",
    },
  },
  decor: {
    brand: false,
    model: false,
    size: {
      enabled: true,
      label: "Dimensions",
      unit: "ft",
      inputType: "dimensions",
      placeholder: "e.g., 3ft x 2ft",
    },
  },
  outdoor: {
    brand: false,
    model: false,
    size: {
      enabled: true,
      label: "Capacity",
      unit: "liters",
      inputType: "text",
      placeholder: "e.g., 1000L",
    },
  },
  tools: {
    brand: false,
    model: false,
    size: {
      enabled: false,
      label: "Size",
      unit: "varies",
      inputType: "text",
    },
  },
  lighting: {
    brand: false,
    model: false,
    size: {
      enabled: false,
      label: "Size",
      unit: "varies",
      inputType: "text",
    },
  },
  baby: {
    brand: true,
    model: false,
    size: {
      enabled: false,
      label: "Size",
      unit: "varies",
      inputType: "text",
    },
  },
  digital_wears: {
    brand: true,
    model: false,
    size: {
      enabled: false,
      label: "Size",
      unit: "varies",
      inputType: "text",
    },
  },
  gaming: {
    brand: true,
    model: false,
    size: {
      enabled: false,
      label: "Size",
      unit: "varies",
      inputType: "text",
    },
  },
  personal_computers: {
    brand: true,
    model: false,
    size: {
      enabled: false,
      label: "Size",
      unit: "varies",
      inputType: "text",
    },
  },
  phones_tabs: {
    brand: true,
    model: false,
    size: {
      enabled: false,
      label: "Size",
      unit: "varies",
      inputType: "text",
    },
  },
  power_solutions: {
    brand: true,
    model: false,
    size: {
      enabled: true,
      label: "Capacity",
      unit: "KVA",
      inputType: "text",
      placeholder: "e.g., 5 KVA, 200AH",
    },
  },
  other: {
    brand: false,
    model: false,
    size: {
      enabled: false,
      label: "Size",
      unit: "varies",
      inputType: "text",
    },
  },
};

/**
 * Get field configuration for a category.
 * Model visibility is gated by {@link isSellListingModelFieldEnabled}.
 */
export function getCategoryFieldConfig(
  category: ItemCategory | string | null | undefined,
): CategoryFieldConfig | null {
  if (!category) return null;
  const base = categoryFieldConfigs[category as ItemCategory] || null;
  if (!base) return null;
  if (!isSellListingModelFieldEnabled() && base.model) {
    return { ...base, model: false };
  }
  return base;
}
